/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { DenseListCard } from '../../../shared/surface-cards/DenseListCard';
import type { CashSessionRow } from '../../infra/cashSessionsRepo';
import { formatRelativeDayTime } from '@shared/vueltoDateTime';
import { useCashSessionMovements } from '../hooks/useCashSessionMovements';
import { useCashSessionDetail } from '../hooks/useCashSessionDetail';
import { CashActivityList } from '../CashActivityList';
import { DEFAULT_PAYMENT_METHOD_LABELS } from '../../../sales/public-api';
import { cashArqueoCopy } from './cashArqueoCopy';
import { formatMoney } from './cashManagement.utils';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { cashStyles } from './cashManagement.styles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

function CashAmountGrid({
  expected,
  counted,
  difference,
}: {
  expected: number;
  counted: number;
  difference: number;
}) {
  return (
    <View style={panelStyles.amountGrid}>
      <View style={panelStyles.amountCell}>
        <Text style={panelStyles.amountLabel}>{cashArqueoCopy.theoreticalBalance}</Text>
        <Text style={panelStyles.amountValue}>{formatMoney(expected)}</Text>
      </View>
      <View style={panelStyles.amountCell}>
        <Text style={panelStyles.amountLabel}>{cashArqueoCopy.countedCash}</Text>
        <Text style={panelStyles.amountValue}>{formatMoney(counted)}</Text>
      </View>
      <View style={panelStyles.amountCell}>
        <Text style={panelStyles.amountLabel}>{cashArqueoCopy.difference}</Text>
        <Text
          style={[
            panelStyles.amountValue,
            {
              color:
                difference === 0
                  ? ticketColors.ok
                  : difference > 0
                    ? ticketColors.warn
                    : ticketColors.bad,
            },
          ]}
        >
          {difference === 0
            ? cashArqueoCopy.noDifference
            : difference > 0
              ? `+${formatMoney(difference)}`
              : formatMoney(difference)}
        </Text>
      </View>
    </View>
  );
}

export function CashSessionDetailPanel({
  s,
  onClose,
  workspaceMode,
  branchId,
  operatorLabel,
}: {
  s: CashSessionRow;
  onClose: () => void;
  workspaceMode?: boolean;
  branchId: string | null;
  operatorLabel: (userId: string | null | undefined) => string | null;
}) {
  const movements = useCashSessionMovements({
    session: s,
    branchId,
    enabled: true,
  });
  // BUG-09: habilitar detail también en workspaceMode para mostrar medios de pago
  const { detail } = useCashSessionDetail(s, true);
  const insets = useSafeAreaInsets();

  const isClosed = s.status === 'closed' && s.closedAt;
  const detailTitle = isClosed ? 'Turno cerrado' : 'Turno abierto';
  const statusSubtitle = isClosed
    ? `Cerrado ${formatRelativeDayTime(s.closedAt!)}` + (s.closedBy ? ` · ${operatorLabel(s.closedBy) ?? s.closedBy}` : '')
    : `Abierto ${formatRelativeDayTime(s.openedAt)}`;

  const diff = s.difference ?? 0;
  const paymentBreakdown = detail?.paymentBreakdown ?? [];

  const movementsError = movements.loadError ? (
    <View style={panelStyles.movementsErrorBanner}>
      <Text style={[styles.caption, { flex: 1 }]}>
        No pudimos cargar los movimientos. Mostramos solo la apertura y el cierre.
      </Text>
      <TouchableOpacity
        onPress={() => void movements.reload()}
        accessibilityRole="button"
        accessibilityLabel="Reintentar movimientos"
        testID="caja.detail.movements-retry"
      >
        <Text style={panelStyles.movementsErrorRetry}>Reintentar</Text>
      </TouchableOpacity>
    </View>
  ) : null;

  // ── Wide: original card layout ──
  if (workspaceMode) {
    const header = (
      <View style={panelStyles.detailHeader}>
        <Text style={panelStyles.detailTitle}>{detailTitle}</Text>
        <TouchableOpacity onPress={onClose} accessibilityLabel="Cerrar detalle" accessibilityRole="button">
          <Ionicons name="close" size={26} color={ticketColors.ink2} />
        </TouchableOpacity>
      </View>
    );

    const meta = (
      <>
        <Text style={styles.caption}>Apertura del turno: {formatRelativeDayTime(s.openedAt)}</Text>
        <Text style={styles.caption}>Fondo inicial: {formatMoney(s.openingAmount)}</Text>
        {s.status === 'closed' && s.closedBy ? (
          <Text style={styles.caption}>
            Cerrado por {operatorLabel(s.closedBy) ?? s.closedBy}
          </Text>
        ) : null}
      </>
    );

    const amounts = (
      <CashAmountGrid
        expected={s.expectedAmount ?? 0}
        counted={s.closingAmount ?? 0}
        difference={diff}
      />
    );

    const note = s.note?.trim() ? (
      <Text style={[styles.caption, { marginTop: spacing.xs }]} numberOfLines={8}>
        Nota: {s.note.trim()}
      </Text>
    ) : null;

    const salesSummaryBlock = detail ? (
      <View style={{ gap: 4, marginTop: spacing.sm }}>
        <Text style={panelStyles.sectionLabel}>Resumen del turno</Text>
        <View style={cashStyles.paymentLineMobile}>
          <Text style={cashStyles.paymentMethodMobile}>Ventas del turno</Text>
          <Text style={cashStyles.paymentAmountMobile}>{detail.salesCount}</Text>
        </View>
        <View style={cashStyles.paymentLineMobile}>
          <Text style={cashStyles.paymentMethodMobile}>{cashArqueoCopy.sessionSalesAmountLabel}</Text>
          <Text style={cashStyles.paymentAmountMobile}>{formatMoney(detail.salesTotal)}</Text>
        </View>
        {detail.outflowsTotal > 0 ? (
          <View style={cashStyles.paymentLineMobile}>
            <Text style={cashStyles.paymentMethodMobile}>Egresos del turno</Text>
            <Text style={cashStyles.paymentAmountMobile}>{formatMoney(detail.outflowsTotal)}</Text>
          </View>
        ) : null}
        {detail.inflowsTotal > 0 ? (
          <View style={cashStyles.paymentLineMobile}>
            <Text style={cashStyles.paymentMethodMobile}>Ingresos del turno</Text>
            <Text style={cashStyles.paymentAmountMobile}>{formatMoney(detail.inflowsTotal)}</Text>
          </View>
        ) : null}
      </View>
    ) : null;

    const paymentBreakdownBlock = detail && paymentBreakdown.length > 0 ? (
      <View style={{ gap: 4, marginTop: spacing.sm }}>
        <Text style={panelStyles.sectionLabel}>Medios de pago</Text>
        {paymentBreakdown.filter((p) => p.total > 0).map((line) => (
          <View key={line.methodId} style={cashStyles.paymentLineMobile}>
            <Text style={cashStyles.paymentMethodMobile}>
              {DEFAULT_PAYMENT_METHOD_LABELS[line.methodId] ?? line.methodId}
            </Text>
            <Text style={cashStyles.paymentAmountMobile}>{formatMoney(line.total)}</Text>
          </View>
        ))}
      </View>
    ) : null;

    const movementsBlock = (
      <View style={panelStyles.sessionMovementsBlock}>
        <Text style={panelStyles.turnoPanelSectionTitle}>Movimientos del turno</Text>
        <Text style={[styles.caption, { marginBottom: spacing.sm }]}>
          Apertura, movimientos de efectivo y cierre de este turno.
        </Text>
        {movementsError}
        <CashActivityList
          items={movements.items}
          loading={movements.loading}
          operatorLabel={operatorLabel}
          layoutWide={workspaceMode}
        />
      </View>
    );

    return (
      <DenseListCard bodyStyle={panelStyles.workspaceDetailCardBody}>
        {header}
        {meta}
        {amounts}
        {note}
        {salesSummaryBlock}
        {paymentBreakdownBlock}
        {movementsBlock}
      </DenseListCard>
    );
  }

  // ── Mobile: clean design bottom sheet content ──
  return (
    <View style={cashStyles.detailSheetContainer}>
      {/* Grabber */}
      <View style={cashStyles.detailSheetGrabber} />

      {/* Header */}
      <View style={cashStyles.detailSheetHeader}>
        <Text style={cashStyles.detailSheetTitle}>{detailTitle}</Text>
        <TouchableOpacity
          style={cashStyles.detailSheetClose}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        >
          <Ionicons name="close" size={16} color={ticketColors.ink3} />
        </TouchableOpacity>
      </View>

      {/* Scrollable content */}
      <ScrollView
        style={cashStyles.detailSheetScroll}
        contentContainerStyle={cashStyles.detailSheetContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {/* Status row */}
        <View style={cashStyles.detailStatusRow}>
          <View style={cashStyles.detailStatusLeft}>
            <Text style={cashStyles.detailStatusTitle} accessibilityRole="header">
              {isClosed ? `Turno ${formatRelativeDayTime(s.openedAt)}` : 'Turno en curso'}
            </Text>
            <Text style={cashStyles.detailStatusSubtitle} numberOfLines={2}>
              {statusSubtitle}
            </Text>
          </View>
          <View
            style={[
              cashStyles.statusPillMobile,
              isClosed ? cashStyles.statusPillMobileWarning : cashStyles.statusPillMobileSuccess,
            ]}
          >
            <Text
              style={[
                cashStyles.statusPillMobileText,
                isClosed ? cashStyles.statusPillMobileTextWarning : cashStyles.statusPillMobileTextSuccess,
              ]}
            >
              {isClosed ? cashArqueoCopy.statusClosed : cashArqueoCopy.statusOpen}
            </Text>
          </View>
        </View>

        {/* Meta block */}
        <View style={cashStyles.detailMetaBlock}>
          <Text style={cashStyles.detailMetaLine}>Apertura: {formatRelativeDayTime(s.openedAt)}</Text>
          <Text style={cashStyles.detailMetaLine}>Fondo inicial: {formatMoney(s.openingAmount)}</Text>
          {isClosed && s.closedBy ? (
            <Text style={cashStyles.detailMetaLine}>Cerrado por: {operatorLabel(s.closedBy) ?? s.closedBy}</Text>
          ) : null}
        </View>

        {/* Arqueo KPI grid (3 cols) */}
        <Text style={panelStyles.sectionLabelMobile}>Arqueo de caja</Text>
        <View style={cashStyles.detailKpiGrid}>
          <View style={cashStyles.detailKpiCell}>
            <Text style={[cashStyles.detailKpiValue, { color: ticketColors.gold }]}>{formatMoney(s.expectedAmount ?? 0)}</Text>
            <Text style={cashStyles.detailKpiLabel}>Esperado</Text>
          </View>
          <View style={cashStyles.detailKpiCell}>
            <Text style={cashStyles.detailKpiValue}>{formatMoney(s.closingAmount ?? 0)}</Text>
            <Text style={cashStyles.detailKpiLabel}>Contado</Text>
          </View>
          <View style={cashStyles.detailKpiCell}>
            <Text
              style={[
                cashStyles.detailKpiValue,
                diff !== 0 && { color: diff > 0 ? ticketColors.warn : ticketColors.bad },
              ]}
            >
              {diff > 0 ? `+${formatMoney(diff)}` : formatMoney(diff)}
            </Text>
            <Text style={cashStyles.detailKpiLabel}>Diferencia</Text>
          </View>
        </View>

        {/* Nota del cierre — pegada a la grilla de arqueo */}
        {s.note?.trim() ? (
          <View style={cashStyles.detailNoteBlock}>
            <Text style={cashStyles.detailNoteLabel}>Nota del cierre</Text>
            <Text style={cashStyles.detailNoteText} numberOfLines={8}>{s.note.trim()}</Text>
          </View>
        ) : null}

        {/* Resumen del turno */}
        {detail ? (
          <View style={{ gap: 4 }}>
            <Text style={panelStyles.sectionLabelMobile}>Resumen del turno</Text>
            <View style={cashStyles.paymentLineMobile}>
              <Text style={cashStyles.paymentMethodMobile}>Ventas del turno</Text>
              <Text style={cashStyles.paymentAmountMobile}>{detail.salesCount}</Text>
            </View>
            <View style={cashStyles.paymentLineMobile}>
              <Text style={cashStyles.paymentMethodMobile}>{cashArqueoCopy.sessionSalesAmountLabel}</Text>
              <Text style={cashStyles.paymentAmountMobile}>{formatMoney(detail.salesTotal)}</Text>
            </View>
          </View>
        ) : null}

        {/* Medios de pago */}
        {detail ? (
          <View style={{ gap: 4 }}>
            <Text style={panelStyles.sectionLabelMobile}>Medios de pago</Text>
            {paymentBreakdown.filter((p) => p.total > 0).map((line) => (
              <View key={line.methodId} style={cashStyles.paymentLineMobile}>
                <Text style={cashStyles.paymentMethodMobile}>
                  {DEFAULT_PAYMENT_METHOD_LABELS[line.methodId] ?? line.methodId}
                </Text>
                <Text style={cashStyles.paymentAmountMobile}>{formatMoney(line.total)}</Text>
              </View>
            ))}
            {paymentBreakdown.every((p) => p.total === 0) ? (
              <Text style={panelStyles.turnoEmptyHint}>Sin ventas en este turno.</Text>
            ) : null}
          </View>
        ) : null}

        {/* Divider */}
        <View style={cashStyles.detailDivider} />

        {/* Movimientos del turno */}
        <Text style={panelStyles.sectionLabelMobile}>Movimientos del turno</Text>
        {movementsError}
        <CashActivityList
          items={movements.items}
          loading={movements.loading}
          operatorLabel={operatorLabel}
          layoutWide={false}
        />
      </ScrollView>

      {/* Footer */}
      <View style={[cashStyles.detailSheetFooter, { paddingBottom: Math.max(insets.bottom, 20) }]}>
        <TouchableOpacity
          style={[cashStyles.btnMobileSecondary, { width: '100%' }]}
          onPress={onClose}
          accessibilityRole="button"
        >
          <Text style={cashStyles.btnMobileSecondaryText}>Cerrar</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export function CashLastClosedSummary({ vm, session }: { vm: import('./cashManagement.types').CashVm; session: CashSessionRow }) {
  const { layoutWebShell } = useContentLayout('dataWide');
  const mobile = !layoutWebShell;
  const diff = session.difference ?? 0;
  const note = session.note?.trim();
  const { detail } = useCashSessionDetail(session, mobile);

  if (mobile) {
    const paymentBreakdown = detail?.paymentBreakdown ?? [];

    return (
      <View style={{ gap: spacing.sm }}>
        {/* Metric row — 4 cards (3+1) */}
        <View style={cashStyles.metricRowMobile}>
          <View style={[cashStyles.metricCardMobile, { minWidth: 80 }]}>
            <Text style={cashStyles.metricLabelMobile}>Fondo inicial</Text>
            <Text style={cashStyles.metricValueMobile}>{formatMoney(session.openingAmount)}</Text>
          </View>
          <View style={[cashStyles.metricCardMobile, { minWidth: 80 }]}>
            <Text style={cashStyles.metricLabelMobile}>Efectivo contado</Text>
            <Text style={cashStyles.metricValueMobile}>{formatMoney(session.closingAmount ?? 0)}</Text>
          </View>
          <View style={[cashStyles.metricCardMobile, { minWidth: 80 }]}>
            <Text style={cashStyles.metricLabelMobile}>{cashArqueoCopy.theoreticalBalance}</Text>
            <Text style={cashStyles.metricValueMobile}>{formatMoney(session.expectedAmount ?? 0)}</Text>
          </View>
          <View style={[cashStyles.metricCardMobile, { minWidth: 80 }]}>
            <Text style={cashStyles.metricLabelMobile}>Diferencia</Text>
            <Text
              style={[
                cashStyles.metricValueMobile,
                diff !== 0 && { color: diff > 0 ? ticketColors.warn : ticketColors.bad },
              ]}
            >
              {diff > 0 ? `+${formatMoney(diff)}` : formatMoney(diff)}
            </Text>
          </View>
        </View>

        {vm.lastClosedOutsideHistoryRange && session.closedAt ? (
          <Text style={panelStyles.turnoMetaHint}>
            Fuera del período del historial. Ampliá el rango para verlo en la lista.
          </Text>
        ) : null}

        {/* Resumen del turno cerrado */}
        <Text style={panelStyles.sectionLabelMobile}>Resumen del turno cerrado</Text>
        <View style={cashStyles.metricRowMobile}>
          <View style={cashStyles.metricCardMobile}>
            <Text style={cashStyles.metricLabelMobile}>Ventas</Text>
            <Text style={cashStyles.metricValueMobile}>{detail?.salesCount ?? 0}</Text>
          </View>
          <View style={cashStyles.metricCardMobile}>
            <Text style={cashStyles.metricLabelMobile}>{cashArqueoCopy.sessionSalesAmountLabel}</Text>
            <Text style={cashStyles.metricValueMobile}>{formatMoney(detail?.salesTotal ?? 0)}</Text>
          </View>
          <View style={cashStyles.metricCardMobile}>
            <Text style={cashStyles.metricLabelMobile}>Egresos</Text>
            <Text style={cashStyles.metricValueMobile}>{formatMoney(detail?.outflowsTotal ?? 0)}</Text>
          </View>
          {(detail?.inflowsTotal ?? 0) > 0 ? (
            <View style={cashStyles.metricCardMobile}>
              <Text style={cashStyles.metricLabelMobile}>Ingresos</Text>
              <Text style={cashStyles.metricValueMobile}>{formatMoney(detail?.inflowsTotal ?? 0)}</Text>
            </View>
          ) : null}
        </View>

        {/* Medios de pago */}
        {detail ? (
          <View style={{ gap: 4 }}>
            <Text style={panelStyles.sectionLabelMobile}>Medios de pago</Text>
            {paymentBreakdown
              .filter((p) => p.total > 0)
              .map((line) => (
                <View key={line.methodId} style={cashStyles.paymentLineMobile}>
                  <Text style={cashStyles.paymentMethodMobile}>
                    {DEFAULT_PAYMENT_METHOD_LABELS[line.methodId] ?? line.methodId}
                  </Text>
                  <Text style={cashStyles.paymentAmountMobile}>{formatMoney(line.total)}</Text>
                </View>
              ))}
            {paymentBreakdown.every((p) => p.total === 0) ? (
              <Text style={panelStyles.turnoEmptyHint}>Sin ventas en este turno.</Text>
            ) : null}
          </View>
        ) : null}

        {/* Notas del cierre */}
        {note ? (
          <View style={cashStyles.fieldMobile}>
            <Text style={cashStyles.fieldLabelMobile}>Notas del cierre</Text>
            <Text style={cashStyles.helperTextMobile}>{note}</Text>
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <View style={{ gap: spacing.sm }}>
      {session.closedAt ? (
        <Text style={styles.caption}>Cerrado {formatRelativeDayTime(session.closedAt)}</Text>
      ) : null}
      {vm.lastClosedByLabel ? <Text style={styles.caption}>Cerrado por {vm.lastClosedByLabel}</Text> : null}
      <CashAmountGrid
        expected={session.expectedAmount ?? 0}
        counted={session.closingAmount ?? 0}
        difference={session.difference ?? 0}
      />
      {vm.lastClosedOutsideHistoryRange && session.closedAt ? (
        <Text style={panelStyles.turnoMetaHint}>
          Fuera del período del historial. Ampliá el rango para verlo en la lista.
        </Text>
      ) : null}
      {session.note?.trim() ? (
        <Text style={styles.caption} numberOfLines={6}>
          Nota: {session.note.trim()}
        </Text>
      ) : null}
    </View>
  );
}
